import { Injectable } from '@angular/core';
import { CategoryTag, CustomProductList, OccCategoryTagsListResponse } from '@catalog/const';
import { CatalogActions, CategoryTagsFacade, OccCategoryTagsService } from '@catalog/data-access';
import { CityService } from '@city/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATION, RouterNavigationAction } from '@ngrx/router-store';
import { decodeUid } from '@shared/helpers';
import { ActivatedRouterStateSnapshot } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

@Injectable()
export class CategoryTagsEffects {
  loadTagsList$: Observable<CatalogActions.LoadCategoryTagsSuccess | CatalogActions.LoadCategoryTagsFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CatalogActions.LOAD_CATEGORY_TAGS),
        distinctUntilKeyChanged('uid'),
        switchMap((action: CatalogActions.LoadCategoryTags) => {
          const { uid } = action;
          const [, categoryCode] = decodeUid(uid);

          return this.occCategoryTagsService.loadTagsList(categoryCode).pipe(
            map(({ tags }: OccCategoryTagsListResponse) => tags),
            map((tags: CategoryTag[]) => new CatalogActions.LoadCategoryTagsSuccess(uid, tags)),
            catchError((e: unknown) => of(new CatalogActions.LoadCategoryTagsFail(categoryCode, e))),
          );
        }),
      ),
    );

  /**
   * Установка активного тега
   *
   * Получаем идентификатор тега из урла страницы
   * Затем подписываемся на событие смены города, чтобы при его срабатывании перезапрашивать активный тег
   */
  setActiveCategoryTagItem$: Observable<CatalogActions.LoadCategoryTagItem> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      map((action: RouterNavigationAction<ActivatedRouterStateSnapshot>) => action.payload.routerState),
      filter((routerState) => routerState.semanticRoute === CustomProductList.SeoProductList),
      map((routerState) => routerState.params.tagSlug),
      filter<string>(Boolean),
      switchMap((tagSlug) =>
        this.cityService.activeBaseStoreCode$.pipe(
          filter<string>(Boolean),
          distinctUntilChanged(),
          map(() => tagSlug),
        ),
      ),
      map((tagSlug) => new CatalogActions.LoadCategoryTagItem(tagSlug)),
    ),
  );

  loadActiveCategoryTagItem$: Observable<
    CatalogActions.LoadCategoryTagItemSuccess | CatalogActions.LoadCategoryTagItemFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogActions.LOAD_CATEGORY_TAG_ITEM),
      map((action: CatalogActions.LoadCategoryTagItem) => action.tagSlug),
      switchMap((tagSlug) =>
        this.occCategoryTagsService.loadTagItem(tagSlug).pipe(
          map((tag: CategoryTag) => new CatalogActions.LoadCategoryTagItemSuccess(tag)),
          catchError((e: unknown) => of(new CatalogActions.LoadCategoryTagItemFail(e))),
        ),
      ),
    ),
  );

  // Данный эффект подгружает теги до загрузки pages
  categoryNavigate$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((action: RouterNavigationAction<ActivatedRouterStateSnapshot>) => action.payload.routerState),
        filter(({ context, params }) => context.type === 'CategoryPage' && !!params.categoryCode),
        map<ActivatedRouterStateSnapshot, string>(({ params }) => params.categoryCode),
        withLatestFrom(this.cityService.activeBaseStoreCode$),
        switchMap(([categoryCode, citySlug]: [string, string]) =>
          this.categoryTagsFacade.getCategoryTags(citySlug, categoryCode).pipe(take(1)),
        ),
        map(() => undefined),
      ),
    {
      dispatch: false,
    },
  );

  constructor(
    private actions$: Actions,
    private occCategoryTagsService: OccCategoryTagsService,
    private cityService: CityService,
    private categoryTagsFacade: CategoryTagsFacade,
  ) {}
}
